import { IconNames, IconSizes, PresetRange } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type {
  AdvancedSearchResultScope,
  SearchFilterCondition,
  SearchFilterConditionCustomAttributeField,
  SearchFilterConditionDate,
  SearchFilterField,
  SearchFilterOperator,
  SearchFilterOperatorBoolean,
  SearchFilterOperatorDate,
  SearchFilterOperatorNullish,
  SearchFilterOperatorNumber,
  SearchFilterOperatorOneToMany,
  SearchFilterOperatorOneToOne,
  SearchFilters,
  SearchFilterStrategy,
} from 'lib-shared-types-api';
import moment from 'moment-timezone';

import { channelAttributes } from 'utils/channels';
import { FeatureFlagName } from 'utils/featureFlagging/featureFlags';
import { userTypeAssets } from 'utils/user';

/** Represents the item type passed to dropdowns. */
export type FilterItemType = {
  id: number | string;
  name: string;
  color?: string;
  icon?: IconNames;
  iconSize?: IconSizes;
};

/** A map of static filter fields and items available to select in the dropdowns. */
export const filterItems: Record<
  'initial_direction' | 'status' | 'initial_channel' | 'user_type',
  FilterItemType[]
> = {
  initial_direction: [
    { id: 'inbound', name: 'Inbound' },
    { id: 'outbound', name: 'Outbound' },
    { id: 'internal', name: 'Internal' },
  ],
  status: [
    { id: 'closed', name: 'Closed' },
    { id: 'pending', name: 'Follow-up' },
    { id: 'open', name: 'Open' },
  ],
  initial_channel: Object.entries(channelAttributes).map(
    ([channel, { name, icon }]) => ({
      id: convertChannelCasing(channel, 'truncate'),
      name,
      icon,
    }),
  ),
  user_type: Object.entries(userTypeAssets)
    .filter(([id]) => id !== 'bot')
    .map(([id, { icon, prettyName }]) => ({
      id,
      name: prettyName,
      icon,
    })),
};

/** A moment object for today's date, starting at 12 AM */
const todayStart = moment().startOf('day');
/** A moment object for today's date, ending at 23:59 PM */
const todayEnd = moment().endOf('day');

/** A list of date range presets used for quick date selections. */
export const dateRangePresets: PresetRange[] = [
  {
    key: 'today',
    label: 'Today',
    startDate: todayStart,
    endDate: todayEnd,
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    startDate: todayStart.clone().subtract(1, 'day'),
    endDate: todayEnd.clone().subtract(1, 'day'),
  },
  {
    key: 'last_7_days',
    label: 'Last 7 days',
    startDate: todayStart.clone().subtract(7, 'day'),
    endDate: todayEnd,
  },
  {
    key: 'last_4_weeks',
    label: 'Last 4 weeks',
    startDate: todayStart.clone().subtract(4, 'week'),
    endDate: todayEnd,
  },
  {
    key: 'last_3_months',
    label: 'Last 3 months',
    startDate: todayStart.clone().subtract(3, 'month'),
    endDate: todayEnd,
  },
];

/** A list of date-time filter fields. */
export const dateFilterFields = [
  'created_at',
  'last_message_created_at',
  'updated_at',
  'assigned_at',
  'closed_at',
] as const satisfies readonly SearchFilterConditionDate['field'][];

/**
 * A list of filter fields whose labels are defined or generated on the
 * client-side.
 */
export const staticFilterFields = [
  ...dateFilterFields,
  'initial_direction',
  'initial_channel',
  'status',
  'duration',
];

/** A map of search page tags and the filter fields they support for filtering. */
export const filterFieldsPerTab: Record<
  AdvancedSearchResultScope,
  SearchFilterField[]
> = {
  conversations: [
    'tags',
    'queue_id',
    'agent_id',
    'requested_by',
    'status',
    'initial_channel',
    'initial_direction',
    'duration',
    'note_message',
    'note_author_id',
    'message_author_id',
    'created_at',
    'updated_at',
    'last_message_created_at',
    'assigned_at',
    'closed_at',
    'numeric_id',
  ],
  notes: ['agent_id', 'queue_id', 'created_at'],
  contacts: ['company_id', 'user_type'],
};

/** Represents information about a filter field rendered in the UI. */
export type FilterFieldAssets = {
  /** The display label of the filter. */
  label: string;
  /** The description of the filtered, for each separate context. */
  description: Partial<Record<AdvancedSearchResultScope, string>>;
  /** The icon of the filter. */
  icon: IconNames;
  /** An optional feature flag required to enable the filter. */
  featureFlag?: FeatureFlagName;
};

/** A map of filter fields and various display attributes. */
export const filterFieldAssets: Record<
  Exclude<SearchFilterField, SearchFilterConditionCustomAttributeField>,
  FilterFieldAssets
> = {
  /** Conversation fields. */
  numeric_id: {
    label: 'ID',
    description: {
      conversations: 'The id of the conversation.',
    },
    icon: 'code',
  },
  status: {
    label: 'Status',
    description: {
      conversations:
        'Whether the conversation is open, closed or in follow up.',
    },
    icon: 'lock',
  },
  initial_channel: {
    label: 'Channel',
    description: {
      conversations: 'The channel the conversation originated from.',
    },
    icon: 'channels',
  },
  initial_direction: {
    label: 'Direction',
    description: {
      conversations: 'Whether this is an inbound or outbound conversation.',
    },
    icon: 'exchange',
  },
  queue_id: {
    label: 'Queue',
    description: {
      conversations: 'The current queue of the conversation.',
      notes: 'The current queue of the conversation with the note.',
    },
    icon: 'users',
  },
  agent_id: {
    label: 'Agent',
    description: {
      conversations: 'The agent currently assigned to the conversation.',
      notes: 'The agent who created the note.',
    },
    icon: 'headset',
  },
  requested_by: {
    label: 'Contact',
    description: {
      conversations: 'The customer contact of the conversation.',
    },
    icon: 'id-card',
  },
  tags: {
    label: 'Tag',
    description: {
      conversations: 'The tags that are currently applied to the conversation.',
    },
    icon: 'tag',
  },
  duration: {
    label: 'Duration',
    description: {
      conversations:
        'The period from when the conversation was created until it was closed the last time, or time of search if the conversation is open.',
    },
    icon: 'clock',
  },
  note_message: {
    label: 'Internal note',
    description: {
      conversations: 'The content of the internal note in the conversation.',
    },
    icon: 'sticky-note',
    featureFlag: 'search-internal-notes-filters',
  },
  note_author_id: {
    label: 'Internal note author',
    description: {
      conversations:
        'The agent who wrote the internal note in the conversation.',
    },
    icon: 'headset',
    featureFlag: 'search-internal-notes-filters',
  },
  message_author_id: {
    label: 'Message author',
    description: {
      conversations: 'The agent who wrote the message in the conversation.',
    },
    icon: 'headset',
    featureFlag: 'search-internal-notes-filters',
  },
  created_at: {
    label: 'Creation date',
    description: {
      conversations: 'The date when the conversation was created.',
      notes: 'The date when the note was added to the conversation.',
    },
    icon: 'calendar-plus',
  },
  updated_at: {
    label: 'Last activity',
    description: {
      conversations:
        'The date when the last activity occurred on the conversation. Conversation activity is defined as one of the following events: internal note added, new message added, tag added, transferred or assigned to a queue or an agent.',
    },
    icon: 'calendar',
  },
  last_message_created_at: {
    label: 'Last message',
    description: {
      conversations:
        'The date when the last message was added to the conversation.',
    },
    icon: 'calendar-plus',
  },
  assigned_at: {
    label: 'Assigned date',
    description: {
      conversations:
        'The latest time the conversation was assigned to an agent.',
    },
    icon: 'calendar',
  },
  closed_at: {
    label: 'Closed date',
    description: {
      conversations: 'The latest time the conversation was closed.',
    },
    icon: 'calendar-times',
  },

  /** Contact fields. */
  id: {
    label: 'ID',
    description: {
      contacts: 'The ID of the contact.',
    },
    icon: 'id-card',
  },
  teams: {
    label: 'Team',
    description: {
      contacts: 'The teams the contact is part of.',
    },
    icon: 'users',
  },
  company_id: {
    label: 'Company',
    description: {
      contacts: 'The company of the contact.',
    },
    icon: 'building',
  },
  user_type: {
    label: 'User category',
    description: {
      contacts: 'The category of the user.',
    },
    icon: 'user',
  },
  is_assigned: {
    label: 'Assigned',
    description: {
      contacts: 'Whether the agent has assigned conversations.',
    },
    icon: 'check-circle',
  },
  is_working: {
    label: 'Working',
    description: {
      contacts: 'Whether the agent is currently working.',
    },
    icon: 'working',
  },
  is_online: {
    label: 'Online',
    description: {
      contacts: 'Whether the agent is currently online.',
    },
    icon: 'circle',
  },
  has_acw: {
    label: 'In wrap-up',
    description: {
      contacts: 'Whether the agent is currently in after-call work.',
    },
    icon: 'clock',
  },
};

/** A map of filter strategies and display attributes. */
export const filterStrategyAssets: Record<
  SearchFilterStrategy,
  { label: string; description: string }
> = {
  all: {
    label: 'ALL',
    description: 'If all of the criteria are met',
  },
  any: {
    label: 'ONE OR MORE',
    description: 'If one or more of the criteria are met',
  },
};

/** The display names of the filter operators. */
export const filterOperatorLabels: Record<SearchFilterOperator, string> = {
  is_one: 'is one of',
  is_not_one: 'is not one of',
  includes_one: 'includes one of',
  excludes_one: 'excludes one of',
  includes_all: 'includes all of',
  excludes_all: 'excludes all of',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  between: 'between',
  since: 'since',
  until: 'until',
  gte: 'longer than or equal to',
  lte: 'shorter than or equal to',
  contains: 'contains',
  not_contains: 'does not contain',
  equals: 'equals',
  not_equals: 'does not equal',
};

/** A map of filter operators grouped by the field data type they can operate on. */
export const filterOperatorGroups = {
  oneToOne: [
    'is_one',
    'is_not_one',
  ] as const satisfies readonly SearchFilterOperatorOneToOne[],

  oneToMany: [
    'includes_one',
    'excludes_one',
    'includes_all',
    'excludes_all',
  ] as const satisfies readonly SearchFilterOperatorOneToMany[],

  nullish: [
    'is_empty',
    'is_not_empty',
  ] as const satisfies readonly SearchFilterOperatorNullish[],

  number: [
    'between',
    'gte',
    'lte',
  ] as const satisfies readonly SearchFilterOperatorNumber[],

  text: [
    'contains',
    'not_contains',
  ] as const satisfies readonly SearchFilterOperator[],

  date: [
    'between',
    'since',
    'until',
  ] as const satisfies readonly SearchFilterOperatorDate[],

  boolean: [
    'equals',
    'not_equals',
  ] as const satisfies readonly SearchFilterOperatorBoolean[],
};

/** Represents a type used to map filter fields and operators. */
type FieldOperators = {
  readonly [TCondition in SearchFilterCondition as TCondition['field']]: TCondition extends {
    [TConditionField in keyof TCondition]: TCondition[TConditionField];
  }
    ? {
        defaultOperator: TCondition['operator'];
        operators: TCondition['operator'][];
      }
    : never;
};

/**
 * A map of filter fields and the operators supported for filtering on those
 * fields.
 */
export const fieldOperators: FieldOperators = {
  /** Conversation fields. */
  numeric_id: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  status: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  initial_channel: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  initial_direction: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  requested_by: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  queue_id: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },
  agent_id: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },
  tags: {
    defaultOperator: 'includes_one',
    operators: [
      ...filterOperatorGroups.oneToMany,
      ...filterOperatorGroups.nullish,
    ],
  },
  duration: {
    defaultOperator: 'between',
    operators: [...filterOperatorGroups.number],
  },
  note_message: {
    defaultOperator: 'contains',
    operators: [...filterOperatorGroups.text],
  },
  note_author_id: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  message_author_id: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  conversation_custom_attribute: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },

  /** Contact fields. */
  id: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  teams: {
    defaultOperator: 'includes_one',
    operators: [
      ...filterOperatorGroups.oneToMany,
      ...filterOperatorGroups.nullish,
    ],
  },
  user_type: {
    defaultOperator: 'is_one',
    operators: [...filterOperatorGroups.oneToOne],
  },
  company_id: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },
  contact_custom_attribute: {
    defaultOperator: 'is_one',
    operators: [
      ...filterOperatorGroups.oneToOne,
      ...filterOperatorGroups.nullish,
    ],
  },

  /** Boolean fields. */
  is_assigned: {
    defaultOperator: 'equals',
    operators: [...filterOperatorGroups.boolean],
  },
  is_working: {
    defaultOperator: 'equals',
    operators: [...filterOperatorGroups.boolean],
  },
  is_online: {
    defaultOperator: 'equals',
    operators: [...filterOperatorGroups.boolean],
  },
  has_acw: {
    defaultOperator: 'equals',
    operators: [...filterOperatorGroups.boolean],
  },

  /** All date fields used should and will always use the same operators. */
  ...dateFilterFields.reduce(
    (out, field) => ({
      ...out,
      [field]: {
        defaultOperator: 'between',
        operators: [...filterOperatorGroups.date],
      },
    }),
    {} as Pick<FieldOperators, (typeof dateFilterFields)[number]>,
  ),
};

/** The initial search filters configuration state. */
export const filtersInitialState: SearchFilters = {
  strategy: 'all',
  conditions: [],
};

/** Error messages displayed when validating filter conditions. */
export const filterValidationErrors = {
  noValue: 'Please select at least one value or remove the filter',
  noDate: 'Please select a date or remove the filter',
  invalidRange: 'Please select both dates or use a different operator',
};
